import { create } from "zustand";
import { createJSONStorage, persist, StateStorage } from "zustand/middleware";

// Width of the Opus Agent drawer, persisted per the localStorage rule: a
// zustand store with the persist middleware, in-memory state as the truth.
// This is a device/UI preference, not signed-in-user data, so logout.tsx
// deliberately does not clear it.
export const DRAWER_DEFAULT_WIDTH = 326;
export const DRAWER_MIN_WIDTH = 320;
export const DRAWER_MAX_WIDTH = 800;
// The drawer also never takes more than this share of the viewport.
export const DRAWER_MAX_VIEWPORT_FRACTION = 0.6;

export const maxDrawerWidthForViewport = (viewportWidth: number): number =>
  Math.max(
    DRAWER_MIN_WIDTH,
    Math.min(
      DRAWER_MAX_WIDTH,
      Math.floor(viewportWidth * DRAWER_MAX_VIEWPORT_FRACTION),
    ),
  );

// CSS form of the viewport cap, applied to the rendered drawer so a width
// persisted on a big screen never crushes the content on a small window —
// and tracks window resizes for free.
export const DRAWER_MAX_WIDTH_CSS = `${DRAWER_MAX_VIEWPORT_FRACTION * 100}vw`;

// The width the drawer actually renders at for a stored preference in a
// given viewport (the store keeps the user's choice; rendering caps it).
export const effectiveDrawerWidth = (
  storedWidth: number,
  viewportWidth: number,
): number =>
  Math.min(
    storedWidth,
    Math.floor(viewportWidth * DRAWER_MAX_VIEWPORT_FRACTION),
  );

// Live clamp (drag, setWidth): pull any number into range.
export const clampDrawerWidth = (
  width: number,
  max: number = DRAWER_MAX_WIDTH,
): number => Math.min(max, Math.max(DRAWER_MIN_WIDTH, Math.round(width)));

// Hydration sanitizer: anything that is not a finite number inside the
// allowed range (corrupt storage, an older schema's bounds) falls back to
// the default instead of being stretched to a bound the user never chose.
export const sanitizeStoredWidth = (value: unknown): number =>
  typeof value === "number" &&
  Number.isFinite(value) &&
  value >= DRAWER_MIN_WIDTH &&
  value <= DRAWER_MAX_WIDTH
    ? Math.round(value)
    : DRAWER_DEFAULT_WIDTH;

type DrawerWidthState = {
  width: number;
  setWidth: (width: number) => void;
  resetWidth: () => void;
};

const noopStorage: StateStorage = {
  getItem: () => null,
  setItem: () => undefined,
  removeItem: () => undefined,
};

const safeLocalStorage: StateStorage = {
  getItem: (name) => {
    try {
      return window.localStorage.getItem(name);
    } catch {
      return null;
    }
  },
  setItem: (name, value) => {
    try {
      window.localStorage.setItem(name, value);
    } catch {
      return;
    }
  },
  removeItem: (name) => {
    try {
      window.localStorage.removeItem(name);
    } catch {
      return;
    }
  },
};

export const useOpusAgentDrawerWidthStore = create<DrawerWidthState>()(
  persist(
    (set) => ({
      width: DRAWER_DEFAULT_WIDTH,
      setWidth: (width) => set({ width: clampDrawerWidth(width) }),
      resetWidth: () => set({ width: DRAWER_DEFAULT_WIDTH }),
    }),
    {
      name: "opus-agent-drawer-width",
      version: 1,
      storage: createJSONStorage(() =>
        typeof window === "undefined" ? noopStorage : safeLocalStorage,
      ),
      partialize: (state) => ({ width: state.width }),
      merge: (persisted, current) => ({
        ...current,
        width: sanitizeStoredWidth(
          (persisted as { width?: unknown } | null)?.width,
        ),
      }),
    },
  ),
);
